import { styled } from '@linaria/react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { CapabilityBucket } from '../../../../api/agents-client/agent-client.type';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { Switch } from '../../../../ui-library/switch/switch';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { getFloraConnectionPath } from '../agent-settings/agent-settings.constants';
import { IconTile } from '../shared/settings/icon-tile';
import {
  ChevronToggleButton,
  ExpandButton,
  ExpandChevron,
  SettingsCard,
  SettingsCardHeader,
} from '../shared/settings/settings-card';
import { getSectionVisual } from './agent-customize.constants';
import { applyCapabilityLocks, getBucketState } from './agent-customize.helpers';
import type { CustomizeHandlers } from './agent-customize.type';
import { GoogleGLogo, Microsoft365Logo } from './brand-icons';
import { CapabilityRow } from './capability-card';

interface CapabilityBucketCardProps {
  bucket: CapabilityBucket;
  enabledTools: Record<string, boolean>;
  handlers: CustomizeHandlers;
}

/**
 * A capability bucket (e.g. Google Drive) rendered as a card.
 *
 * When the underlying account is connected, the header shows the account badge,
 * a bucket-wide toggle-all, and an expand chevron; expanding lists the child
 * capabilities. When NOT connected, the toggles are replaced by a "Connect"
 * button that routes to the connection's settings page — there is nothing to configure
 * until the account is linked.
 *
 * @param {CapabilityBucketCardProps} props - The bucket, draft slice, and handlers
 * @returns {JSX.Element} The bucket card
 */
export function CapabilityBucketCard({ bucket, enabledTools, handlers }: CapabilityBucketCardProps) {
  const [expanded, setExpanded] = useState(true);
  const navigate = useNavigate();
  const visual = getSectionVisual(bucket.id);
  const isGoogle = bucket.id === 'google';
  const isOffice = bucket.id === 'office';
  const badgeBg = isGoogle || isOffice ? 'var(--sky-lightest)' : visual.badgeBg;

  const badge = (
    <IconTile background={badgeBg} size="lg">
      {isGoogle ? (
        <GoogleGLogo size={24} />
      ) : isOffice ? (
        <Microsoft365Logo size={24} />
      ) : (
        <Icon name={visual.icon} size={24} color={visual.color} />
      )}
    </IconTile>
  );

  if (!bucket.connected) {
    return (
      <Card>
        <SettingsCardHeader>
          {badge}
          <Info>
            <Text size="medium" weight="medium" color="var(--ink-darkest)">
              {bucket.name}
            </Text>
            <Text size="small" color="var(--ink-lighter)">
              Connect your {bucket.name} account to use these capabilities.
            </Text>
          </Info>
          <Button variant="primary" size="sm" onClick={() => navigate(getFloraConnectionPath(bucket.id))}>
            Connect
          </Button>
        </SettingsCardHeader>
      </Card>
    );
  }

  // Force-enable and hide the toggle for any capability locked on by a sibling
  // (e.g. Office "General" while Excel or Word is enabled). Drives both the
  // bucket-wide toggle-all and the per-capability rows from the same state.
  const capabilities = applyCapabilityLocks(bucket.capabilities, enabledTools);
  const state = getBucketState({ ...bucket, capabilities }, enabledTools);

  return (
    <Card>
      <SettingsCardHeader>
        <ExpandButton
          type="button"
          aria-expanded={expanded}
          aria-label={`${bucket.name} capabilities`}
          onClick={() => setExpanded((prev) => !prev)}
        >
          {badge}
          <Info>
            <Text size="medium" weight="medium" color="var(--ink-darkest)">
              {bucket.name}
            </Text>
            {bucket.account && (
              <Text size="small" color="var(--ink-lighter)">
                {bucket.account}
              </Text>
            )}
          </Info>
        </ExpandButton>
        {state.hasOptional && (
          <Tooltip variant="black" label={`Enable or disable all ${bucket.name} capabilities`}>
            <SwitchWrap>
              <Switch
                on={state.allOn}
                size="lg"
                ariaLabel={`Toggle all ${bucket.name} capabilities`}
                onToggle={() => handlers.setTools(state.optionalNames, !state.allOn)}
              />
            </SwitchWrap>
          </Tooltip>
        )}
        <ChevronToggleButton
          type="button"
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${bucket.name} capabilities`}
          onClick={() => setExpanded((prev) => !prev)}
        >
          <ExpandChevron expanded={expanded} size={18} color="var(--ink-lighter)" />
        </ChevronToggleButton>
      </SettingsCardHeader>

      {expanded && (
        <Body>
          {capabilities.map((capability) => (
            <CapabilityRow
              key={capability.id}
              capability={capability}
              enabledTools={enabledTools}
              handlers={handlers}
            />
          ))}
        </Body>
      )}
    </Card>
  );
}

// Cards sit in the settings section's stack, which spaces them.
const Card = SettingsCard;

const Info = styled.div`
  flex: 1;
  min-width: 0;
  line-height: 1.3;
`;

const SwitchWrap = styled.span`
  display: inline-flex;
  flex-shrink: 0;
`;

const Body = styled.div`
  padding: 0 20px 10px;
`;
